import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Package, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";

/**
 * 2026-06 · 从产品一览表挑选寄样产品（寄样清单详情页的「选择产品」按钮）。
 * 后端 POST /sample-lists/{id}/items 已支持 overview_ids，重复编号会自动跳过。
 */
export function SampleProductPicker({ open, onOpenChange, listId, onAdded }) {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/product-overview", {
        params: { page: 1, page_size: 60, ...(query ? { search: query } : {}) },
      });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { if (open) fetchRows(); }, [open, fetchRows]);
  useEffect(() => { if (!open) { setPicked(new Set()); setSearch(""); } }, [open]);

  const toggle = (id) => setPicked((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sample-lists/${listId}/items`, { overview_ids: [...picked] });
      toast.success(t("sample.addedToast").replace("{n}", data.added).replace("{s}", data.skipped));
      onAdded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="sample-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{t("sample.pickProducts")}</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t("sample.pickSearchPlaceholder")} className="pl-8 h-9"
            data-testid="sample-picker-search" />
        </div>

        <div className="max-h-[52vh] overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" size={20} /></div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground" data-testid="sample-picker-empty">
              {t("po.empty")}
            </p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="sample-picker-grid">
              {rows.map((r) => {
                const on = picked.has(r.id);
                return (
                  <li key={r.id}>
                    <button type="button" onClick={() => toggle(r.id)}
                      className={`w-full text-left rounded-md border p-1.5 transition-colors ${on ? "border-brand bg-brand/5" : "border-border hover:bg-secondary/50"}`}
                      data-testid={`sample-picker-item-${r.item_number}`}>
                      <div className="relative h-24 bg-white rounded-sm overflow-hidden flex items-center justify-center">
                        {r.photo_url ? (
                          <img src={photoThumb(r.photo_url, "sm")} alt={r.item_number}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const i = e.currentTarget;
                              if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(r.photo_url, "sm"); }
                            }} />
                        ) : <Package size={20} className="text-muted-foreground" />}
                        {on && (
                          <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-brand text-white flex items-center justify-center">
                            <Check size={12} />
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-mono text-[11px] font-bold truncate">{r.item_number}</p>
                      <p className="text-[10px] text-muted-foreground line-clamp-2">{r.description || r.date || ""}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="items-center">
          <span className="mr-auto text-xs text-muted-foreground" data-testid="sample-picker-count">
            {t("sample.pickedCount").replace("{n}", picked.size)}
          </span>
          <Button onClick={add} disabled={busy || picked.size === 0}
            className="bg-brand hover:bg-brand-hover" data-testid="sample-picker-add">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} {t("sample.addSelected")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
